import type { CardId } from '@kenocyber/shared';

/**
 * **Todo o equilíbrio das dezasseis cartas, num sítio só** (gate V28H.L).
 *
 * ## O estatuto destes números: PROVISÓRIOS, e ditos uma vez aqui
 *
 * Nenhum valor deste ficheiro é uma decisão de design tomada por quem joga. O
 * dono autorizou explicitamente que fossem escolhidos provisoriamente, coerentes
 * com o equilíbrio existente, para as cartas poderem existir; e a única coisa
 * que os torna aceitáveis é estarem TODOS aqui, num ficheiro que se lê de uma
 * vez e se muda numa edição.
 *
 * O que **não** é provisório, e não pode ser mexido sem falar com o dono, são as
 * quatro decisões que o contrato do catálogo (gate V28H.B) e o próprio dono já
 * tinham fechado, e que estão marcadas **APROVADO** onde aparecem:
 *
 * - o Leviatã é pilotável **13 s** a contar da entrada;
 * - o Manto Espectral dura **9 s**, e atacar encerra-o;
 * - o raio do Catolé é literalmente **6x** o da Bomba Atômica;
 * - os Super Canhões são exactamente **dois**, durante **6 s**.
 *
 * ## As três regras transversais, decididas aqui e uma vez
 *
 * ### 1. Uma penalidade não se soma: fica a MAIOR, e empatar renova
 *
 * Duas Lentidões sobre o mesmo casco não dão 100% de lentidão. Vale a de maior
 * magnitude; se as magnitudes forem iguais, vale a que acabar mais tarde. É
 * exactamente a disciplina que os artefactos já usam - "só a cópia de maior
 * nível concede o efeito" - e escolhê-la aqui é escolher a que o jogo já tem em
 * vez de inventar uma segunda.
 *
 * A alternativa - somar - tem um defeito que não é de gosto: três jogadores de
 * uma equipa com a mesma carta desligariam um adversário permanentemente, e
 * nenhuma quantidade de afinação conserta isso.
 *
 * ### 2. Nenhuma penalidade passa dos 80%
 *
 * {@link CARD_DEBUFF_CAP_BPS}. Uma redução de 100% é um controlo total, e um
 * duelo em que um lado não pode agir não é um duelo. O tecto é sobre o VALOR
 * EFECTIVO e não sobre o que a carta declara, portanto uma carta futura mais
 * forte continua a ser lida como mais forte e continua a bater no mesmo tecto.
 *
 * ### 3. Uma invocação morre com quem a invocou
 *
 * O Drone e o Leviatã desaparecem no tique em que o dono morre, se desliga ou
 * abandona, e no tique em que a partida acaba. É a regra mais simples que é
 * sempre verdadeira, e é a única que não deixa uma entidade órfã numa
 * instantânea - o que seria uma entidade que uma reconexão ressuscita e ninguém
 * controla.
 *
 * O Leviatã tem um caso a mais, e é APROVADO: uma vez que alguém entre, ele é
 * destruído 13 s depois **aconteça o que acontecer**, incluindo se o piloto
 * morrer lá dentro.
 *
 * ## As unidades, e porque não há uma única fracção
 *
 * - **tempo** em milissegundos inteiros, e todos múltiplos de **50** - o tique
 *   do duelo, onde as dezasseis valem. Na caça, que avança 16 ms, nem todos
 *   caem na grelha: os **13 s do Leviatã** são aprovados e não se dobram para
 *   caberem nela. O que vale lá é a garantia do relógio, provada em
 *   `effects.test.ts` - um temporizador acaba sempre, acaba TARDE e nunca cedo,
 *   e por menos de um tique;
 * - **percentagens** em basis points inteiros (10 000 = 100%);
 * - **distâncias** em basis points de uma referência que cada modo tem - o raio
 *   do casco - e nunca em píxeis. É a mesma disciplina dos Super Canhões (gate
 *   V28H.H), e é o que faz uma carta ter a mesma FORMA numa arena de duelo e
 *   numa caçada;
 * - **dano** em basis points do dano-base autoritativo de quem lançou. Uma carta
 *   nunca traz um número de dano próprio: ela multiplica o de quem a usa, o que
 *   a mantém relevante em qualquer ponto da progressão sem escalar sozinha.
 */

/** O tecto de qualquer penalidade, em bps do valor afectado. Ver §2 acima. */
export const CARD_DEBUFF_CAP_BPS = 8_000;

/** O que uma carta faz, escolhido de uma lista fechada. */
export type CardEffectKind =
  /** Um deslocamento imediato na direcção da mira. */
  | 'dash'
  /** Um temporizador sobre quem a lançou. */
  | 'self_buff'
  /** Um temporizador sobre um alvo inimigo, escolhido pelo servidor. */
  | 'target_debuff'
  /** Um temporizador sobre TODOS os inimigos de uma vez. */
  | 'area_debuff'
  /** Uma carga que termina num feixe. Só a Lança Solar (ADR 0065). */
  | 'charged_beam'
  /** Uma carga que termina num projétil explosivo. */
  | 'charged_projectile'
  /** Um projétil teleguiado com explosão. */
  | 'guided_blast'
  /** Uma lâmina que orbita quem a lançou. */
  | 'orbit_blade'
  /** Canhões temporários montados no casco. */
  | 'temporary_cannons'
  /** Uma entidade que nasce no mundo. */
  | 'summon';

/** Que penalidade um temporizador aplica. `null` para os que não penalizam. */
export type CardDebuffKind =
  /** Velocidade de MOVIMENTO reduzida. */
  | 'move_speed'
  /** Velocidade de ATAQUE reduzida. */
  | 'attack_speed'
  /** Dano causado reduzido. */
  | 'damage_dealt'
  /** Cura recebida reduzida. */
  | 'healing_received'
  /** Parado, e sem disparar. */
  | 'frozen';

/** Que benefício um temporizador aplica a quem o lançou. */
export type CardBuffKind =
  /** Velocidade de movimento aumentada. */
  | 'move_speed'
  /** Não pode ser escolhido como alvo. */
  | 'untargetable'
  /** Invisível para o adversário. Atacar encerra-o. */
  | 'cloaked';

/** O equilíbrio de uma carta. Campos a `null` são os que ela não usa. */
export interface CardBalance {
  readonly effect: CardEffectKind;
  /**
   * Quanto tempo o efeito dura, em ms. `0` para um efeito instantâneo.
   *
   * O nível NUNCA mexe aqui: o contrato do catálogo é explícito - "o upgrade só
   * toca no cooldown", e uma carta de nível 6 é a mesma carta, mais vezes.
   */
  readonly durationMs: number;
  /** A penalidade que ela aplica, ou `null`. */
  readonly debuff: CardDebuffKind | null;
  /** O benefício que ela dá a quem a lança, ou `null`. */
  readonly buff: CardBuffKind | null;
  /** A magnitude da penalidade ou do benefício, em bps. `0` quando não há. */
  readonly magnitudeBps: number;
  /**
   * O dano de cada golpe, em bps do dano-base de quem lançou.
   *
   * Do DONO e nunca da carta: é o que faz uma carta valer o mesmo no primeiro
   * duelo e no milésimo, sem uma segunda curva de progressão a existir.
   */
  readonly damageBps: number;
  /** Milissegundos entre dois golpes, para os efeitos que cobram em parcelas. */
  readonly hitIntervalMs: number;
  /**
   * O alcance ou o raio, em bps do raio do casco de quem lançou.
   *
   * Do casco e não da arena: uma nave grande tem um Turbilhão grande, que é a
   * mesma proporção que os Super Canhões já usam.
   */
  readonly reachBps: number;
  /** Tempo de carga antes de o efeito sair, em ms. `0` para quem não carrega. */
  readonly chargeMs: number;
  /**
   * **Esta carta viaja como um projéctil antes de aplicar o efeito** (V28H.W).
   *
   * `ice_sphere` é o Congelamento, `ice_star` é a Lentidão e `shock_bolt` é o
   * Curto-Circuito - as três descrições aprovadas usam um verbo de LANÇAMENTO
   * ("lança uma esfera", "lança uma estrela", "**dispara** energia elétrica"), e
   * lançar quer dizer que alguma coisa atravessa a arena e pode falhar.
   *
   * Ausente é o efeito no instante da ordem, que é o que a Marca da Caveira faz:
   * a descrição dela diz "**marca** o inimigo", e marcar não é disparar.
   *
   * As letras servem também a CENA: uma esfera, uma estrela e um raio não se
   * desenham da mesma maneira, e adivinhar qual é pelo efeito seria a arte a
   * sair de uma etiqueta técnica.
   */
  readonly bolt: 'ice_sphere' | 'ice_star' | 'shock_bolt' | null;
  /** Se a carga prende o movimento. Só a Lança Solar, por descrição aprovada. */
  readonly chargeLocksMovement: boolean;
  /** Quantas entidades ou peças ela cria. `0` para quem não cria nada. */
  readonly count: number;
}

/** Um molde, para cada entrada escrever só o que lhe diz respeito. */
const NONE = {
  durationMs: 0,
  debuff: null,
  buff: null,
  magnitudeBps: 0,
  damageBps: 0,
  hitIntervalMs: 0,
  reachBps: 0,
  chargeMs: 0,
  bolt: null,
  chargeLocksMovement: false,
  count: 0,
} as const satisfies Omit<CardBalance, 'effect'>;

/**
 * A tabela, indexada pelo id.
 *
 * `Record<CardId, ...>` e não um array: assim o COMPILADOR exige as dezasseis e
 * recusa uma décima sétima, exactamente como o catálogo faz. Um id novo em
 * `cards.ts` sem linha aqui é erro de compilação, e não uma carta que chega ao
 * duelo sem saber o que faz.
 */
export const CARD_BALANCE: Readonly<Record<CardId, CardBalance>> = Object.freeze({
  /**
   * **Arranque** — avança depressa na direcção para onde a nave aponta.
   *
   * 900 000 mu num quarto de segundo: cerca de 2,6 s de viagem normal gastos em
   * cinco tiques. Não atravessa cascos - ele usa o mesmo movimento de sempre,
   * portanto o bloqueio do gate V28H.K trava-o - e **não dá invulnerabilidade**,
   * porque a descrição aprovada diz "avança rapidamente" e mais nada.
   */
  card_dash: {
    ...NONE,
    effect: 'dash',
    /*
     * **600 ms, e não 250** (gate V28H.V).
     *
     * O dono viu o avanço e disse que parecia um teleporte. A distância não era
     * o problema sozinha: 250 ms são cinco tiques, e cinco tiques a atravessar
     * meio ecrã leem-se como um salto entre dois sítios.
     *
     * O empurrão de uma bomba - que ele deu como referência do que é suave -
     * anda 360 000 mu em 350 ms, ou 51 400 por tique. Com 600 ms e a distância
     * triplicada, este avanço anda 65 000 por tique: a mesma ordem de grandeza,
     * portanto a mesma leitura de coisa que se desloca em vez de aparecer.
     */
    durationMs: 600,
    /*
     * **Dez raios de casco em 250 ms** (gate V28H.V), e não um terço de um.
     *
     * A auditoria mediu o que estava: 3 461 bps do raio do casco são 9 000 mu, e
     * um casco anda 17 000 mu POR TIQUE - portanto o "arranque" empurrava a nave
     * um décimo do que ela andaria a pé nos mesmos cinco tiques. Ele era mais
     * lento do que andar, e por isso não se via.
     *
     * **Quarenta e cinco** raios desde a terceira avaliação: 1 170 000 mu.
     * Repartidos pelos doze tiques dão 97 500 mu por tique - quase seis vezes o
     * passo normal, e duas vezes o empurrão de uma bomba.
     *
     * ## Porque ele continuava a parecer curto depois de triplicar
     *
     * Porque **a câmara está presa à própria nave**. Ela é centrada na posição
     * visual do casco local a cada quadro - está escrito em `followCamera` - e
     * portanto quem avança não se vê a atravessar coisa nenhuma: o que anda é o
     * mundo por baixo dele. Num fundo estrelado, isso lê-se como um salto.
     *
     * Ir mais longe ajuda, porque o mundo passa mais depressa. O que resolve é a
     * ARTE: o rasto, as brasas e a onda de choque ficam NO MUNDO, e é vendo-os
     * afastarem-se que o jogador mede o que percorreu. No ecrã do ADVERSÁRIO,
     * onde a câmara está presa a outra nave, o avanço sempre se leu como
     * deslocamento - e é essa a leitura correcta.
     *
     * Continua a NÃO ser um teletransporte, e é a mesma mecânica que o garante:
     * o avanço vai em `drift`, tique a tique, pelo mesmo caminho que uma
     * explosão usa para empurrar - portanto as paredes, as pilastras e os outros
     * cascos travam-no exactamente onde travam um passo a pé.
     */
    reachBps: 450_000,
  },

  /**
   * **Sobremarcha** — mais velocidade de movimento, temporariamente.
   *
   * +50%, cinco segundos. Deliberadamente ABAIXO da runa de Velocidade, que dá
   * +50% sobre a base por 15 000 bps: a runa é um objecto que se disputa no
   * mapa, e uma carta que a superasse tornaria a disputa inútil.
   */
  card_speed_burst: {
    ...NONE,
    effect: 'self_buff',
    buff: 'move_speed',
    durationMs: 5_000,
    magnitudeBps: 5_000,
  },

  /**
   * **Lança Solar** — o único laser (ADR 0065).
   *
   * Um segundo e meio a carregar, preso ao chão e à vista de toda a gente; um
   * segundo e meio a arder, a cobrar de 200 em 200 ms. Seis parcelas a 70% do
   * dano-base, e **cada parcela imprime o seu próprio número** - é isso que
   * mantém o §"cada golpe lógico imprime o seu número" verdadeiro sobre um
   * feixe.
   *
   * O alcance é dezoito raios de casco. Longo, e tem de ser: a carta paga-o com
   * imobilidade, e uma lança de curto alcance seria só uma maneira lenta de
   * morrer.
   */
  card_solar_lance: {
    ...NONE,
    effect: 'charged_beam',
    durationMs: 1_200,
    damageBps: 7_000,
    hitIntervalMs: 200,
    /**
     * **ZERO de propósito**: o alcance desta carta é uma medida de ARENA.
     *
     * Ele viveu aqui, uma proporção do casco para os dois modos, e produzia um
     * feixe 3,7 vezes mais curto no duelo do que na caça - porque a nave do
     * duelo ocupa 2 % do ecrã e a da caça 13 %. Mudou-se para
     * `HUNT_SOLAR_LANCE_REACH_BPS` e `PVP_SOLAR_LANCE_REACH_BPS`, ao lado das
     * espessuras, que já se tinham dividido pela mesma razão.
     *
     * Fica a zero e não com um número antigo: um número aqui seria lido um dia
     * por engano, e daria a versão que o dono reprovou.
     */
    reachBps: 0,
    chargeMs: 1_500,
    chargeLocksMovement: true,
  },

  /**
   * **Esfera Ígnea** — carrega e lança uma grande esfera de fogo explosiva.
   *
   * **Quinze segundos de carga, e setenta cascos de raio na carga cheia**
   * (gates V28H.V e V28H.W). Eram 1,2 s e seis cascos; passaram por 9 s e 14
   * cascos, e o dono voltou a avaliá-la a segurar a tecla: "tem que ficar muito
   * maior... a ideia é a bola de fogo poder ficar do tamanho da explosão da
   * bomba, tem que ser realmente grande".
   *
   * Nove segundos é muito tempo a pé quieto, e é isso que a carta passou a ser:
   * um compromisso longo e visível, que o adversário vê a crescer e tem tempo de
   * evitar. Quem não quiser pagá-lo solta quando quiser - a esfera sai com o que
   * ela vale nesse instante, e é isso que `cardSphereRadiusFor` exprime.
   *
   * O dano CHEIO não subiu com o tempo. Ele já era 260% do dano-base, e a carga
   * mais longa comprou área e não número: uma carta que multiplicasse as duas
   * coisas de uma vez seria uma decisão de equilíbrio disfarçada de decisão de
   * arte.
   */
  card_charged_fireball: {
    ...NONE,
    effect: 'charged_projectile',
    /*
     * **Prende o movimento durante a carga** (gate V28H.U).
     *
     * Passou a prender por decisão do dono, e ela tem a mesma lógica da Lança
     * Solar: quem carrega uma coisa que cresce está a comprometer-se, e o
     * adversário tem de poder ver o compromisso e sair da linha. Apontar
     * continua livre - o que se prende é andar, e não mirar.
     */
    chargeLocksMovement: true,
    damageBps: 26_000,
    /*
     * **O alcance mudou-se para os dois ARENAS** (gate V28H.Z), como o da Lança.
     *
     * Ele foi afinado a olhar para o duelo, e chegou a setenta raios de casco. A
     * caça tem 1280 unidades de largura e um casco de 96: setenta raios ali são
     * duas vezes e meia a arena inteira - a bola deixaria de ser uma bola para
     * ser o mapa. As duas arenas têm escalas diferentes, e um número só não pode
     * servir as duas.
     *
     * Fica a zero e não com um número antigo, exactamente como
     * {@link CARD_BALANCE}.card_solar_lance fez pela mesma razão: um número aqui
     * seria lido um dia por engano, e daria uma versão que ninguém aprovou.
     */
    reachBps: 0,
    /*
     * **A carga mudou-se para os dois ARENAS** (gate V28I.C), como o alcance.
     *
     * Elas deixaram de ser a mesma carta em dois sítios: no duelo carrega até
     * quinze segundos com a nave presa, e é o preço da maior bola do jogo; na
     * caça carrega **sozinha em 400 ms**, sem exigir que se segure nada, porque
     * lá a nave nem sequer é conduzida e prendê-la quinze segundos era prender o
     * jogador ao ecrã.
     *
     * Fica a ZERO e não com um dos dois números, pela mesma razão que o alcance:
     * um número aqui seria lido um dia por engano, e daria a versão que ninguém
     * aprovou naquele modo.
     */
    chargeMs: 0,
    count: 1,
  },

  /**
   * **Leviatã Aéreo** — invoca um veículo com duas entradas e seis canhões.
   *
   * **APROVADO: 13 s a contar da ENTRADA**, e a destruição é automática. O que é
   * provisório é o resto: ele espera 20 s por um piloto antes de se desfazer
   * sozinho, tem seis canhões a 55% do dano-base cada, e dispara de 250 em
   * 250 ms.
   *
   * A escala sai da vida da nave que o criou, com um mínimo grande - também
   * aprovado - e vive em `pvp/leviathan.ts`, que é onde a geometria dele mora.
   */
  /**
   * **Leviatã Aéreo** — a estrutura que outra nave pilota.
   *
   * **Oito salvas por segundo** (decisão do dono, gate V28H.X), e não quatro:
   * "cada salva de tiros atira 8x por segundo, a menos que o jogador que a
   * utilize tenha velocidade de ataque superior - aí a velocidade de ataque do
   * helicóptero será igual à da nave que o pilota".
   *
   * O "a menos que" vive na sala, que é quem conhece o piloto: este número é o
   * CHÃO da cadência, e quem atira mais depressa leva a sua consigo.
   */
  card_leviathan: {
    ...NONE,
    effect: 'summon',
    durationMs: 13_000,
    damageBps: 5_500,
    hitIntervalMs: 125,
    reachBps: 200_000,
    count: 6,
  },

  /**
   * **Turbilhão de Lâminas** — uma lâmina gigante que gira à volta da nave.
   *
   * Seis segundos, dois raios e meio de casco, e um golpe de 90% do dano-base
   * por alvo a cada 400 ms. O intervalo é por ALVO e não por lâmina: sem isso,
   * um adversário encostado levaria um golpe por tique.
   */
  card_blade_storm: {
    ...NONE,
    effect: 'orbit_blade',
    durationMs: 6_000,
    damageBps: 9_000,
    hitIntervalMs: 400,
    /*
     * **Seis raios de casco**, e o número foi MEDIDO e não escolhido.
     *
     * Começou em 2,5 - uma lâmina duas vezes e meia a nave, que lida como
     * "gigante". No duelo funcionava; na CAÇA nunca apanhava nada, e uma prova
     * mostrou porquê: ali a nave está no fundo e os mobs morrem à distância,
     * portanto quase nenhum chega a 120 unidades dela.
     *
     * Seis raios são um terço da altura da arena da caça e continuam a ser um
     * corpo-a-corpo no duelo. É a mesma carta a valer nos dois sítios, que é o
     * que a proporção existe para conseguir.
     */
    reachBps: 60_000,
    count: 1,
  },

  /**
   * **Catolé** — uma Bomba Atômica teleguiada colossal.
   *
   * **APROVADO: o raio é literalmente 6x o da Bomba Atômica da joia**, e é a
   * razão de a arena ter tido de crescer antes desta carta (gate V28H.K).
   * `reachBps` diz isso em basis points, e `pvp/catole.ts` lê-o do raio real da
   * joia em vez de repetir o número.
   *
   * 750% do dano-base e um empurrão grande. Sessenta segundos de arrefecimento
   * ao nível 1 pagam-no.
   */
  card_catole: {
    ...NONE,
    effect: 'guided_blast',
    damageBps: 75_000,
    /*
     * **Oitenta e um raios de casco**, que é o que "6x a Bomba Atômica" dá.
     *
     * A Bomba vale três raios de casco vezes 450 bps de crescimento - 13,5
     * raios - e seis vezes isso são 81. Em basis points, 810 000.
     *
     * Quem CALCULA não usa este campo: usa `PVP_CATOLE_BLAST_RADIUS_MU`, que lê
     * a constante da joia e a multiplica por seis, para a palavra "6x"
     * continuar verdadeira se a Bomba mudar. Este número existe para a tabela
     * se ler de uma vez, e uma prova compara os dois - foi ela que apanhou este
     * campo dez vezes abaixo do que devia.
     */
    reachBps: 810_000,
    count: 1,
  },

  /**
   * **Drone de Ataque** — segue o dono, caça, dispara e pode explodir em cima.
   *
   * Quinze segundos, 30% da vida máxima do dono, 60% do dano-base a cada 500 ms,
   * e um kamikaze de 300% quando o tempo acaba ou quando ele é destruído. O
   * sprite é **temporariamente o do chefe do mapa 1**, por decisão do dono.
   *
   * Os ataques PRIMÁRIOS dele activam as joias de quem o invocou (gate V28H.B);
   * a colisão kamikaze não activa nada.
   */
  card_attack_drone: {
    ...NONE,
    effect: 'summon',
    durationMs: 15_000,
    damageBps: 6_000,
    hitIntervalMs: 500,
    reachBps: 120_000,
    count: 1,
  },

  /**
   * **Malware** — bloqueia a cura de TODOS os inimigos.
   *
   * **−50%** de cura recebida desde o gate V28H.W, e eram −60%: o contrato desta
   * sessão diz "a cura recebida pelos inimigos é reduzida em 50%", e um número
   * escrito no contrato ganha ao número que estava.
   *
   * Oito segundos, aplicado no instante do lançamento a quem estiver vivo do
   * outro lado. Uma aura que perseguisse quem entrasse depois seria outra carta.
   */
  card_malware: {
    ...NONE,
    effect: 'area_debuff',
    debuff: 'healing_received',
    durationMs: 8_000,
    magnitudeBps: 5_000,
  },

  /**
   * **Congelamento** — paralisa o alvo, e impede os ataques dele.
   *
   * A descrição aprovada dizia "**pode** também impedir seus ataques", e um
   * "pode" não é uma regra enquanto ninguém escrever a probabilidade. O gate
   * V28H.W escreveu-a: **30%**, decidida pelo servidor.
   *
   * E a duração deixou de ser fixa: **entre dois e quatro segundos**, também
   * sorteada pela sala. Os dois números vivem em `pvp/balance.ts` - eles são
   * regra de arena, e esta carta só existe no duelo - e o que fica aqui é o
   * MÍNIMO, que é o que uma esfera vale sempre.
   *
   * Curto de propósito: parar alguém é o efeito mais forte que este catálogo
   * tem.
   */
  card_freeze: {
    ...NONE,
    effect: 'target_debuff',
    debuff: 'frozen',
    /* O MÍNIMO do sorteio: a sala soma-lhe zero, um ou dois segundos. */
    durationMs: 2_000,
    magnitudeBps: 10_000,
    /*
     * **Sem alcance nesta tabela** (gate V28H.W): ela LANÇA.
     *
     * Aqui estavam 300 000 bps - trinta raios de casco - que era o alcance de um
     * efeito instantâneo. A arena abre com os dois cascos a cinco vezes e meia
     * isso, portanto a carta era recusada em silêncio no início de todos os
     * duelos: nem dardo, nem cooldown, nem aviso.
     *
     * Uma carta que lança não tem alcance de ESCOLHA - tem alcance de projéctil,
     * e esse é do dardo e vive em `pvp/balance.ts`, como o do Catolé. O zero que
     * `NONE` dá aqui quer dizer "não é uma decisão desta tabela"; quem lança
     * pergunta por `PVP_CARD_UNLIMITED_REACH_BPS`.
     */
    /* Uma esfera de gelo teleguiada, e já não um efeito instantâneo (V28H.W). */
    bolt: 'ice_sphere',
  },

  /**
   * **Lentidão** — uma estrela de gelo teleguiada: −50% de movimento.
   *
   * A duração deixou de ser fixa no gate V28H.W: **entre três e seis segundos**,
   * sorteada pela sala. O que fica aqui é o mínimo; os degraus vivem em
   * `pvp/balance.ts`, ao lado dos do Congelamento.
   *
   * Os −50% não se movem: eles estavam aprovados e o contrato repete-os.
   */
  card_slow: {
    ...NONE,
    effect: 'target_debuff',
    debuff: 'move_speed',
    durationMs: 3_000,
    magnitudeBps: 5_000,
    /* Sem alcance nesta tabela, como a esfera, e pela mesma razão (V28H.W). */
    bolt: 'ice_star',
  },

  /**
   * **Curto-Circuito** — energia elétrica: −40% de cadência, seis segundos.
   *
   * **Ele DISPARA** (gate V28H.X), e não toca. A descrição aprovada diz "dispara
   * energia elétrica que reduz temporariamente a velocidade de ataque do alvo",
   * e disparar é o mesmo verbo que pôs as duas cartas de gelo a lançar um corpo
   * no gate anterior. Os números - os 40% e os seis segundos - não se mexem.
   */
  card_attack_break: {
    ...NONE,
    effect: 'target_debuff',
    debuff: 'attack_speed',
    durationMs: 6_000,
    magnitudeBps: 4_000,
    /* Sem alcance nesta tabela: quem lança pergunta pelo alcance do projéctil. */
    bolt: 'shock_bolt',
  },

  /**
   * **Marca da Caveira** — marca o inimigo: −35% do dano dele, seis segundos.
   *
   * **Ela NÃO lança nada** (gate V28H.X), e é a única das quatro com alvo que
   * não lança: "marca o inimigo e reduz temporariamente o dano que ele causa".
   * Marcar não é disparar, e inventar-lhe um projéctil seria escrever uma
   * decisão que ninguém tomou.
   *
   * O que muda é o ALCANCE. Os 300 000 bps eram trinta raios de casco - 780 000
   * mu - e a arena do duelo abre com os dois cascos a 4 200 000 mu: a carta era
   * recusada em silêncio no princípio de todos os duelos, exactamente como as
   * duas de gelo estavam antes do gate anterior.
   *
   * Agora é a DISTÂNCIA DE ABERTURA em raios de casco, arredondada para cima -
   * 4 200 000 / 26 000, ou seja 162 cascos - e escrita como o número que é para
   * esta tabela continuar a não conhecer a arena. É o alcance mais curto que
   * torna a carta utilizável no instante em que um duelo começa, e nada mais do
   * que isso: quem se afastar sai dele.
   */
  card_damage_break: {
    ...NONE,
    effect: 'target_debuff',
    debuff: 'damage_dealt',
    durationMs: 6_000,
    magnitudeBps: 3_500,
    reachBps: 1_620_000,
  },

  /**
   * **Manto Espectral** — invisível.
   *
   * **APROVADO: 9 s, e atacar encerra o efeito.** O que é decidido aqui é o
   * resto, e são quatro coisas: levar dano **não** revela - a descrição diz que
   * atacar encerra, e só isso; nada **escolhe** um casco invisível como alvo,
   * nem um míssil nem um drone, pela mesma razão que um jogador não escolhe; o
   * adversário não vê **nada**, nem um brilho, porque um manto que se visse era
   * outra carta; e um tiro que por acaso passe no sítio certo **acerta na
   * mesma** - nove segundos em que nada aterra seriam imunidade, e esta carta é
   * furtividade.
   */
  card_cloak: {
    ...NONE,
    effect: 'self_buff',
    buff: 'cloaked',
    durationMs: 9_000,
    magnitudeBps: 10_000,
  },

  /**
   * **Bastião** — inalvejável, quatro segundos.
   *
   * Inalvejável e não invulnerável, e a linha entre as duas é o que a carta é:
   * **nenhum projétil aterra** nestes quatro segundos - num duelo os tiros são
   * apontados à mão, e uma carta que só travasse mísseis e drones seria uma
   * *perfect* de cinquenta segundos que quase nunca faz nada - mas **uma
   * explosão, uma hemorragia e um veneno continuam a cobrar**. Uma explosão não
   * escolhe ninguém, cobre uma área; uma parcela de veneno já tem o seu alvo
   * desde que foi colocada. É essa a fraqueza dele, e ela é deliberada.
   */
  card_bastion: {
    ...NONE,
    effect: 'self_buff',
    buff: 'untargetable',
    durationMs: 4_000,
    magnitudeBps: 10_000,
  },

  /**
   * **Super Canhões** — dois canhões enormes, à cadência da nave.
   *
   * **APROVADO e já implementado** (gates V28H.H e V28H.J). Os números vivem em
   * `cards/abilities.ts` desde então e não se movem: esta entrada existe para a
   * tabela ser completa e para o compilador poder exigir as dezasseis.
   */
  card_super_cannons: {
    ...NONE,
    effect: 'temporary_cannons',
    durationMs: 6_000,
    count: 2,
  },
});

/** O equilíbrio desta carta, ou `null` para um id que o catálogo não conhece. */
export function cardBalance(definitionId: string): CardBalance | null {
  return (CARD_BALANCE as Record<string, CardBalance | undefined>)[definitionId] ?? null;
}

/**
 * Aplica uma penalidade a um valor, com o tecto da §2.
 *
 * Inteiro do princípio ao fim, com uma única truncagem: o duelo re-simula-se e
 * uma vírgula flutuante aqui faria duas execuções da mesma janela discordarem no
 * último bit.
 */
export function cardReduce(value: number, reductionBps: number): number {
  const capped = Math.min(Math.max(reductionBps, 0), CARD_DEBUFF_CAP_BPS);
  return Math.trunc((value * (10_000 - capped)) / 10_000);
}

/**
 * A magnitude que vale quando duas penalidades do mesmo tipo se encontram.
 *
 * A MAIOR, e nunca a soma. Ver §1: somar deixaria três jogadores de uma equipa
 * desligarem um adversário para sempre, e isso não se afina.
 */
export function cardStrongerDebuff(current: number, incoming: number): number {
  return Math.max(current, incoming);
}

/**
 * **A força de uma carga, em basis points** (gate V28H.N).
 *
 * ## Porque isto existe
 *
 * A Lança Solar é carregada **enquanto a tecla está premida**, e é contrato
 * aprovado: a espessura, a intensidade e o dano crescem com o tempo que o
 * jogador aguentou. Uma carga de duração fixa - que foi o que esteve escrito
 * por engano - tira ao jogador a única decisão que a carta tem.
 *
 * Cheia é 10 000, e é o que uma carga levada até ao fim vale.
 */
export const CARD_CHARGE_FULL_BPS = 10_000;

/**
 * O piso: um toque na tecla ainda dispara, e dispara fraco.
 *
 * **PROVISÓRIO.** 25% é a escolha, e a razão é que zero seria uma tecla que não
 * faz nada - indistinguível de uma falha - e um piso alto tornaria a carga
 * decorativa. Com 25%, largar de imediato custa o arrefecimento inteiro por um
 * quarto do efeito, o que é uma decisão má e não uma decisão impossível.
 */
export const CARD_CHARGE_MIN_BPS = 2_500;

/**
 * Quanto vale uma carga que foi largada com `remainingMs` por cumprir.
 *
 * Deriva do que FALTA e não do que passou, porque é `chargeRemainingMs` que a
 * instantânea guarda. Uma carta sem carga responde sempre cheia: ela não tem
 * nada para graduar, e graduá-la a zero apagaria o efeito das outras quinze.
 */
export function cardChargeStrengthBps(chargeMs: number, remainingMs: number): number {
  if (chargeMs <= 0) {
    return CARD_CHARGE_FULL_BPS;
  }
  const held = Math.max(0, Math.min(chargeMs, chargeMs - remainingMs));
  const raw = Math.round((held * CARD_CHARGE_FULL_BPS) / chargeMs);
  return Math.max(CARD_CHARGE_MIN_BPS, Math.min(CARD_CHARGE_FULL_BPS, raw));
}

/**
 * Escala um valor pela força da carga, em inteiros.
 *
 * `Math.round` e não uma divisão de vírgula flutuante deixada por aí: o dano e
 * a espessura atravessam a instantânea e a re-simulação do servidor, e um
 * arredondamento diferente dos dois lados é uma dessincronização.
 */
export function cardScaleByCharge(value: number, strengthBps: number): number {
  const clamped = Math.max(0, Math.min(CARD_CHARGE_FULL_BPS, strengthBps));
  return Math.round((value * clamped) / CARD_CHARGE_FULL_BPS);
}

/**
 * A fracção da espessura que a carga mínima já vale.
 *
 * **PROVISÓRIO.** O feixe não começa infinitamente fino: 40% da espessura na
 * carga mínima e 100% na cheia. Um feixe de um pixel não se vê, e "a espessura
 * comunica o nível de carga" precisa de uma espessura para comunicar.
 */
export const CARD_BEAM_MIN_WIDTH_BPS = 4_000;

/**
 * A espessura de um feixe com esta carga, dada a espessura CHEIA.
 *
 * Interpola entre {@link CARD_BEAM_MIN_WIDTH_BPS} e cheio - e é a MESMA função
 * que decide quem o feixe apanha e o que a cena desenha. Duas expressões seriam
 * um feixe pintado mais gordo do que aquilo por que ele cobra.
 */
/**
 * **A fracção do raio que a esfera vale no início da carga** (gates V28H.V e
 * V28H.W).
 *
 * **PROVISÓRIO**, e muito mais baixa do que a do feixe: 3%. Um feixe tem de se
 * ver desde o primeiro instante - é a arma inteira, e um feixe de um pixel não é
 * nada - e uma esfera que começa pequena é uma esfera a COMEÇAR, que foi o que o
 * dono pediu ao ver a última: "ela já começa bem grande; tem que começar o
 * carregamento pequena e crescer até o tamanho final".
 *
 * Três por cento de um raio de setenta cascos ainda é uma bola de dois cascos -
 * ou seja, visível. O que ela não é, e não pode ser, é comparável ao que sai no
 * fim.
 */
export const CARD_SPHERE_MIN_RADIUS_BPS = 300;

/**
 * **O raio de uma esfera com esta carga, dado o raio CHEIO.**
 *
 * A gémea de {@link cardBeamWidthFor}, com o chão da esfera - e uma função à
 * parte por uma razão que já mordeu neste projecto: o feixe e a esfera partiam a
 * mesma, e afinar uma afinava a outra em silêncio. São duas cartas.
 *
 * A MESMA função decide o que a explosão apanha, o que o corpo em voo mede e o
 * tamanho da bola que cresce à frente da nave enquanto o dedo está em cima da
 * tecla. Três expressões seriam três oportunidades de a bola desenhada mentir
 * sobre a bola que fere.
 */
/**
 * **Quanto da carga já foi cumprido, SEM o chão da carga mínima.**
 *
 * A gémea crua de {@link cardChargeStrengthBps}, e existe para uma coisa só: a
 * bola desenhada enquanto o dedo está em cima da tecla.
 *
 * O chão existe porque um toque curto tem de produzir alguma coisa - é a regra
 * aprovada da carta - mas aplicá-lo ao DESENHO congelava a bola no tamanho
 * mínimo durante o primeiro quarto da carga, e depois punha-a a crescer. Uma
 * bola parada durante quase quatro segundos é exactamente o que o dono viu
 * quando disse que ela "já começa bem grande".
 *
 * O desenho nunca promete MAIS do que aquilo que sai; no primeiro quarto promete
 * um pouco menos, e é a escolha certa das duas.
 */
export function cardChargeProgressBps(chargeMs: number, remainingMs: number): number {
  if (chargeMs <= 0) {
    return CARD_CHARGE_FULL_BPS;
  }
  const held = Math.max(0, Math.min(chargeMs, chargeMs - remainingMs));
  return Math.max(
    0,
    Math.min(CARD_CHARGE_FULL_BPS, Math.round((held * CARD_CHARGE_FULL_BPS) / chargeMs)),
  );
}

/**
 * **Quais cartas são OFENSIVAS** (gate V28H.V).
 *
 * ## O que esta lista decide
 *
 * Uma coisa só, e é o Manto Espectral: **activar qualquer uma destas encerra a
 * invisibilidade**, no instante em que a ordem é aceite. A decisão é do dono, e
 * a palavra dela é "atacar" - não "acertar".
 *
 * ## Porque uma lista, e não uma pergunta por carta
 *
 * Porque a alternativa era espalhar `cardBreakCloak` por cada habilidade que
 * fere, e isso são doze sítios onde a décima terceira se esquece de aparecer -
 * exactamente o modo de falhar que este ficheiro já apanhou três vezes com os
 * `if` por carta. A fronteira de activação consulta esta lista uma vez, e
 * nenhuma habilidade sabe que o Manto existe.
 *
 * ## Porque não é derivada do EFEITO
 *
 * Porque o efeito não chega para separá-las. A Sobremarcha e o Bastião são
 * `self_buff` como o Manto, mas a Lentidão e o Congelamento são
 * `target_debuff` - e o Arranque é um `dash` que não toca em ninguém enquanto
 * a Esfera Ígnea é um `charged_projectile` que rebenta. O que separa as duas
 * famílias é a INTENÇÃO, e intenção não se deriva de uma etiqueta técnica.
 *
 * As quatro que ficam de fora - Arranque, Sobremarcha, Bastião e o próprio
 * Manto - não fazem nada a ninguém: elas movem ou protegem quem as usa. Um
 * jogador encoberto pode fugir, acelerar e proteger-se sem se revelar, e isso é
 * a carta a funcionar.
 */
const OFFENSIVE_CARDS: ReadonlySet<string> = new Set([
  'card_solar_lance',
  'card_charged_fireball',
  'card_leviathan',
  'card_blade_storm',
  'card_catole',
  'card_attack_drone',
  'card_malware',
  'card_freeze',
  'card_slow',
  'card_attack_break',
  'card_damage_break',
  'card_super_cannons',
]);

/**
 * Esta carta é ofensiva?
 *
 * Um id que a lista não conhece responde `false`, e é a resposta certa: uma
 * carta que este build não conhece não pode ter sido activada por ele.
 */
export function cardIsOffensive(definitionId: string): boolean {
  return OFFENSIVE_CARDS.has(definitionId);
}

/**
 * **Que fracção do raio da explosão é o CORPO da esfera** (gate V28H.Z).
 *
 * ## Porque vive aqui, e não ao lado do desenho
 *
 * Porque ele deixou de ser um número de arte. Ele era: a cena desenhava a bola a
 * 65% do raio da explosão e a sala fazia-a colidir com um raio fixo e pequeno -
 * uma bola de setenta cascos passava por cima de uma nave e só rebentava quando
 * o CENTRO dela chegava a oitenta e cinco mil. O dono viu isso e disse o que
 * tinha de acontecer: "a caixa de colisão com a esfera tem que ser bem maior,
 * crescer junto com a bola de fogo".
 *
 * A partir daqui é um número só, lido pelos dois lados: o que se vê é o que
 * apanha, em qualquer carga e nos dois modos. Dois números seriam duas
 * oportunidades de a bola voltar a mentir.
 */
export const CARD_SPHERE_BODY_BPS = 6_500;

/**
 * O raio do CORPO de uma esfera cuja explosão terá este raio.
 *
 * Truncado, porque do lado da sala isto é uma medida inteira que atravessa a
 * instantânea e é recalculada na re-simulação: uma divisão de vírgula flutuante
 * arredondada de maneiras diferentes nos dois lados é uma dessincronização.
 */
export function cardSphereBodyRadius(blastRadius: number): number {
  return Math.trunc((blastRadius * CARD_SPHERE_BODY_BPS) / 10_000);
}

/**
 * **Que fracção desse corpo é que FERE** (gate V28I.A).
 *
 * Oitenta por cento, e a diferença é deliberada. A esfera desenhada não acaba
 * numa aresta: os últimos pixéis dela são orla luminosa - o clarão somado por
 * cima e a raiz da coroa - e fazer a colisão chegar até lá punha-a a rebentar
 * enquanto ainda se via espaço entre a bola e o casco. Foi o que o dono viu:
 * "explode e causa dano antes de encostar por completo no adversário".
 *
 * Oitenta por cento é onde a massa acaba e a orla começa. O que se perde é o
 * halo; o que se ganha é a bola ter de encostar mesmo.
 */
export const CARD_SPHERE_HIT_BPS = 8_000;

/**
 * O raio a que uma esfera destas APANHA: a parte sólida do corpo dela.
 *
 * Uma função à parte da de cima e não um segundo número solto, porque as duas
 * têm de continuar amarradas: quem afinar o tamanho desenhado afina isto com
 * ele, e a bola nunca volta a ferir a uma distância que ninguém vê.
 */
export function cardSphereHitRadius(blastRadius: number): number {
  return Math.trunc((cardSphereBodyRadius(blastRadius) * CARD_SPHERE_HIT_BPS) / 10_000);
}

export function cardSphereRadiusFor(fullRadius: number, strengthBps: number): number {
  const clamped = Math.max(0, Math.min(CARD_CHARGE_FULL_BPS, strengthBps));
  const span = CARD_CHARGE_FULL_BPS - CARD_SPHERE_MIN_RADIUS_BPS;
  /*
   * **Pequena ao princípio, e a acelerar** (gate V28H.W): `x²`.
   *
   * A curva anterior era `2x - x²` - depressa ao princípio e a abrandar - e o
   * dono viu o resultado: "ela já começa bem grande". Com um raio final de
   * setenta cascos, uma curva que entrega 75% a meio do caminho começa perto do
   * fim, e o que se via era uma bola quase feita a inchar um bocadinho.
   *
   * Invertida, a leitura é a que a carta quer contar: nasce uma brasa, e são os
   * últimos segundos - os caros, os que deixam a nave parada - que fazem dela a
   * maior coisa do ecrã.
   *
   * Em INTEIROS, e é a razão de estar escrita assim em vez de `** 2` ou de uma
   * potência qualquer: o raio é mecânico, atravessa a instantânea e é
   * recalculado pelo servidor quando ele re-simula a janela. `Math.pow` é
   * definido pela implementação - Node e o Chromium discordam no último bit - e
   * um bit de diferença aqui é uma dessincronização. Os produtos intermédios
   * ficam bem abaixo de 2^53, portanto a aritmética é exacta.
   */
  const eased = (clamped * clamped) / CARD_CHARGE_FULL_BPS;
  const factor = CARD_SPHERE_MIN_RADIUS_BPS + Math.round((span * eased) / CARD_CHARGE_FULL_BPS);
  return (fullRadius * factor) / CARD_CHARGE_FULL_BPS;
}

export function cardBeamWidthFor(fullWidth: number, strengthBps: number): number {
  const clamped = Math.max(0, Math.min(CARD_CHARGE_FULL_BPS, strengthBps));
  const span = CARD_CHARGE_FULL_BPS - CARD_BEAM_MIN_WIDTH_BPS;
  const factor = CARD_BEAM_MIN_WIDTH_BPS + Math.round((span * clamped) / CARD_CHARGE_FULL_BPS);
  return (fullWidth * factor) / CARD_CHARGE_FULL_BPS;
}
